import React, { useState, useMemo, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import CreatableSelect from 'react-select/creatable';
import Select from 'react-select';
import { apiClient } from '../../../components/api/ApiClient';

const initialItemState = {
    id: null,
    productId: '',
    name: '',
    count: null,
    amount: '',
    measurement: '',
    weight: '',
    currencyType: 'IRR',
    unitPrice: 0,
    currencyAmount: '',
    currencyPrice: '',
    rialValue: '',
    currencyValue: '',
    priceBeforeSale: 0,
    salePrice: 0,
    priceAfterSale: 0,
    taxPercent: 0,
    taxPrice: 0,
    otherTaxS: '',
    otherTaxR: '',
    otherTax: 0,
    otherLegalS: '',
    otherLegalR: '',
    otherLegalFunds: 0,
    constructionFee: '',
    sellerProfit: '',
    commission: '',
    sumCSC: '',
    constructionFeeNum: '',
    cashContribution: '',
    vpp: '',
    totalPrice: 0,
    diffRialCurrency: '',
    carat: '',
    taxSource: '',
};

const stripLeadingZeros = (value) => {
    if (value === '' || value === null || value === undefined) return 0;
    const strVal = String(value);
    if (strVal === '0' || strVal === '00') return 0;
    const num = parseFloat(strVal);
    if (isNaN(num)) return 0;
    return num;
};

const parseNumericInput = (value) => {
    if (value === '' || value === null || value === undefined) return 0;
    const num = parseFloat(value);
    return isNaN(num) ? 0 : Math.max(0, Math.trunc(num));
};

// حداکثر 18 رقم صحیح و 8 رقم اعشار — خروجی string برای حفظ دقت
const parseAmountInput = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    const str = String(value);
    const dotIdx = str.indexOf('.');
    let intPart = dotIdx >= 0 ? str.slice(0, dotIdx) : str;
    let decPart = dotIdx >= 0 ? str.slice(dotIdx + 1) : null;
    intPart = intPart.replace(/[^0-9]/g, '').slice(0, 18) || '0';
    if (intPart !== '0') intPart = intPart.replace(/^0+/, '') || '0';
    if (decPart !== null) {
        decPart = decPart.replace(/[^0-9]/g, '').slice(0, 8);
        return `${intPart}.${decPart}`;
    }
    return intPart;
};

function ItemsAndServicesStep({ formData, onDataChange, calculatedTotals, readOnly, isShow = () => true, isRequired = () => false, ...rest }) {
    const [showModal, setShowModal] = useState(false);
    const [newItem, setNewItem] = useState(initialItemState);
    const [isEditing, setIsEditing] = useState(false);
    const [availableProducts, setAvailableProducts] = useState([]);
    const [measurementUnits, setMeasurementUnits] = useState([]);
    const [currencies, setCurrencies] = useState([]);

    useEffect(() => {
        const fetchMeasurements = async () => {
            try {
                const response = await apiClient('/internal-network/api/measurement/');
                if (response && Array.isArray(response)) {
                    setMeasurementUnits(response);
                } else if (response && response.data && Array.isArray(response.data)) {
                    setMeasurementUnits(response.data);
                }
            } catch (error) {
                console.error("Error fetching measurements:", error);
            }
        };

        const fetchCurrencies = async () => {
             try {
                const response = await apiClient('/account/api/currencies/');
                if (response && Array.isArray(response)) {
                    setCurrencies(response);
                } else if (response && response.data && Array.isArray(response.data)) {
                    setCurrencies(response.data);
                }
            } catch (error) {
                console.error("Error fetching currencies:", error);
            }
        };

        fetchMeasurements();
        fetchCurrencies();
    }, []);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const response = await apiClient('/user-preferences/api/user-product/');



                let products = [];
                if (Array.isArray(response)) {
                    products = response;
                } else if (response && Array.isArray(response.data)) {
                    products = response.data;
                } else if (response && Array.isArray(response.results)) {
                    products = response.results;
                }

                setAvailableProducts(products);
            } catch (error) {
                console.error("Error fetching products:", error);
            }
        };

        fetchProducts();
    }, []);


    const productOptions = useMemo(() => {
        return availableProducts?.map(p => ({
            value: p.goods_and_services?.identity,
            label: `${p.goods_and_services?.identity} - ${p.goods_and_services?.name}`,
            product: p
        })) || [];
    }, [availableProducts]);

    const handleOpenModal = (item = null) => {
        if (item) {
            setNewItem(item);
            setIsEditing(true);
        } else {
            setNewItem(initialItemState);
            setIsEditing(false);
        }
        setShowModal(true);
    };


    const handleSelectChange = (newValue) => {

        if (!newValue) {
            setNewItem(prev => ({ ...prev, productId: '' }));
            return;
        }


        if (newValue.product) {
            const selectedProduct = newValue.product;
            setNewItem(prev => ({
                ...prev,
                productId: selectedProduct.goods_and_services?.identity,
                name: selectedProduct.goods_and_services?.name || prev.name,
                taxPercent: parseFloat(selectedProduct.goods_and_services?.taxPercent || 0),
            }));
        } else {

            setNewItem(prev => ({ ...prev, productId: newValue.value }));
        }
    };

    const calculateItemCosts = (item, invoiceAlgo = formData?.invoiceAlgo) => {
        const amount = Math.max(0, parseFloat(item.amount ?? item.count) || 0);
        const unitPrice = Math.max(0, parseFloat(item.unitPrice || 0));
        const taxPercent = Math.max(0, parseFloat(item.taxPercent || 0));
        const salePrice = Math.max(0, parseFloat(item.salePrice || 0));
        const otherTax = Math.max(0, parseFloat(item.otherTax || 0));
        const otherLegalFunds = Math.max(0, parseFloat(item.otherLegalFunds || 0));

        // Gold fields
        const constructionFee = parseFloat(item.constructionFee || 0);
        const sellerProfit = parseFloat(item.sellerProfit || 0);
        const commission = parseFloat(item.commission || 0);
        const sumCSC = constructionFee + sellerProfit + commission;

        // Currency fields
        const currencyAmount = parseFloat(item.currencyAmount || 0);
        const currencyPrice = parseFloat(item.currencyPrice || 0);
        const taxSource = parseFloat(item.taxSource || 0);
        const rialValue = parseFloat(item.rialValue || 0);

        // 1. Calculate priceBeforeSale
        let priceBeforeSale = 0;
        if (invoiceAlgo === 'Second') {
            priceBeforeSale = Math.trunc(currencyAmount * currencyPrice);
        } else if (invoiceAlgo === 'Sixth') {
            priceBeforeSale = unitPrice;
        } else {
            priceBeforeSale = Math.trunc(amount * unitPrice);
        }

        // 2. Calculate priceAfterSale
        let priceAfterSale = 0;
        if (invoiceAlgo === 'Third') {
            priceAfterSale = Math.trunc((priceBeforeSale + sumCSC) - salePrice);
        } else {
            priceAfterSale = Math.trunc(Math.max(0, priceBeforeSale - salePrice));
        }

        // 3. Calculate taxPrice
        let taxPrice = 0;
        if (invoiceAlgo === 'Second') {
            taxPrice = Math.trunc((taxSource * taxPercent) / 100);
        } else if (invoiceAlgo === 'Third') {
            taxPrice = Math.trunc(((sumCSC * 10) / 100) + ((priceBeforeSale * taxPercent) / 100));
        } else {
            taxPrice = Math.trunc((priceAfterSale * taxPercent) / 100);
        }

        // 4. Calculate totalPrice
        let totalPrice = 0;
        if (invoiceAlgo === 'Seventh') {
            totalPrice = Math.trunc(rialValue + taxPrice + otherTax + otherLegalFunds);
        } else {
            totalPrice = Math.trunc(priceAfterSale + taxPrice + otherTax + otherLegalFunds);
        }

        // 5. Calculate cashContribution and vpp
        const cashContribution = parseFloat(item.cashContribution || 0) || totalPrice;
        let vpp = 0;
        if (priceAfterSale > 0) {
            vpp = Math.trunc((taxPrice * cashContribution) / priceAfterSale);
        } else {
            vpp = taxPrice;
        }

        return {
            sumCSC,
            priceBeforeSale,
            priceAfterSale: Math.max(0, priceAfterSale),
            taxPrice: Math.max(0, taxPrice),
            totalPrice: Math.max(0, totalPrice),
            cashContribution,
            vpp,
        };
    };

    const handleSaveItem = () => {
        if (!newItem.productId || !newItem.name) {
            alert("لطفاً شناسه و عنوان را وارد کنید.");
            return;
        }

        const requiredCheckErrors = [];
        const fieldsToCheck = [];
        if (isShow('amount')) fieldsToCheck.push('amount');
        if (isShow('unitPrice')) fieldsToCheck.push('unitPrice');
        if (isShow('weight')) fieldsToCheck.push('weight');
        if (isShow('currency')) fieldsToCheck.push('currencyType');
        if (isShow('currencyAmount')) fieldsToCheck.push('currencyAmount');
        if (isShow('currencyPrice')) fieldsToCheck.push('currencyPrice');
        if (isShow('rialValue')) fieldsToCheck.push('rialValue');
        if (isShow('currencyValue')) fieldsToCheck.push('currencyValue');
        if (isShow('diffRialCurrency')) fieldsToCheck.push('diffRialCurrency');
        if (isShow('carat')) fieldsToCheck.push('carat');
        if (isShow('taxSource')) fieldsToCheck.push('taxSource');
        if (isShow('constructionFee')) fieldsToCheck.push('constructionFee');
        if (isShow('sellerProfit')) fieldsToCheck.push('sellerProfit');
        if (isShow('commission')) fieldsToCheck.push('commission');
        if (isShow('constructionFeeNum')) fieldsToCheck.push('constructionFeeNum');
        if (isShow('cashContribution')) fieldsToCheck.push('cashContribution');
        if (isShow('vpp')) fieldsToCheck.push('vpp');

        fieldsToCheck.forEach(field => {
            const configName = field === 'currencyType' ? 'currency' : field;
            if (isRequired(configName) && (newItem[field] === '' || newItem[field] === null || newItem[field] === undefined)) {
                requiredCheckErrors.push(configName);
            }
        });

        if (requiredCheckErrors.length > 0) {
            alert("لطفاً تمامی فیلدهای الزامی را پر کنید.");
            return;
        }

        const calculated = calculateItemCosts(newItem);
        const itemToSave = { ...newItem, ...calculated, productId: String(newItem.productId) };

        let updatedItems;
        if (isEditing) {
            updatedItems = formData.productItems.map(item => item.id === newItem.id ? itemToSave : item);
        } else {
            itemToSave.id = Date.now();
            updatedItems = [...(formData.productItems || []), itemToSave];
        }

        onDataChange({ productItems: updatedItems });
        setShowModal(false);
    };

    const handleRemoveItem = (idToRemove) => {
        const updatedItems = (formData.productItems || []).filter(item => item.id !== idToRemove);
        onDataChange({ productItems: updatedItems });
    };

    const renderAddItemModal = () => (
        <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg" scrollable>
            <Modal.Header closeButton>
                <Modal.Title>
                    {readOnly ? 'مشاهده کالا/خدمت' : (isEditing ? 'ویرایش کالا/خدمت' : 'افزودن کالا/خدمت جدید')}
                    {!readOnly && <span className="text-danger">*</span>}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="row">
                    <div className="col-md-6 mb-3">
                        <label className="form-label">شناسه کالا/خدمت {!readOnly && <span className="text-danger">*</span>}</label>
                        <CreatableSelect
                            classNamePrefix="react-select"
                            isClearable={!readOnly}
                            isSearchable
                            isDisabled={readOnly}
                            placeholder="شناسه کالا را وارد یا انتخاب کنید..."
                            options={productOptions}
                            onChange={handleSelectChange}
                            value={
                                newItem.productId
                                    ? productOptions.find(opt => opt.value === newItem.productId) || { value: newItem.productId, label: newItem.productId }
                                    : null
                            }
                            formatCreateLabel={(inputValue) => `استفاده از "${inputValue}" به عنوان شناسه`}
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    borderColor: '#dfe3e7',
                                    borderRadius: '0.375rem',
                                    minHeight: '38px',
                                    backgroundColor: readOnly ? '#e9ecef' : '#fff',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        borderColor: '#86b7fe'
                                    }
                                }),
                                menu: (base) => ({
                                    ...base,
                                    zIndex: 9999,
                                    textAlign: 'right',
                                    direction: 'rtl'
                                }),
                                option: (base) => ({
                                    ...base,
                                    textAlign: 'right',
                                    direction: 'rtl'
                                }),
                                placeholder: (base) => ({
                                    ...base,
                                    textAlign: 'right',
                                    direction: 'rtl'
                                }),
                                input: (base) => ({
                                    ...base,
                                    textAlign: 'right',
                                    direction: 'rtl'
                                }),
                                singleValue: (base) => ({
                                    ...base,
                                    textAlign: 'right',
                                    direction: 'rtl'
                                }),
                            }}
                        />
                    </div>
                    <div className="col-md-6 mb-3">
                        <label className="form-label">عنوان {!readOnly && <span className="text-danger">*</span>}</label>
                        <input type="text" className="form-control" name="name" value={newItem.name || ''} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} disabled={readOnly} />
                    </div>
                    {isShow('measurement') && (
                        <div className="col-md-3 mb-3">
                            <label className="form-label">واحد اندازه گیری {isRequired('measurement') && !readOnly && <span className="text-danger">*</span>}</label>
                            <Select
                                classNamePrefix="react-select"
                                options={measurementUnits.map(u => ({ value: u.code, label: u.name }))}
                                value={measurementUnits.find(u => u.code === newItem.measurement) ? { value: newItem.measurement, label: measurementUnits.find(u => u.code === newItem.measurement).name } : null}
                                onChange={(opt) => setNewItem({ ...newItem, measurement: opt ? opt.value : '' })}
                                placeholder="انتخاب کنید..."
                                isDisabled={readOnly}
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        borderColor: '#dfe3e7',
                                        borderRadius: '0.375rem',
                                        minHeight: '38px',
                                        backgroundColor: readOnly ? '#e9ecef' : '#fff',
                                        direction: 'rtl',
                                        textAlign: 'right',
                                    }),
                                    menu: (base) => ({ ...base, zIndex: 9999, direction: 'rtl', textAlign: 'right' }),
                                    option: (base) => ({ ...base, direction: 'rtl', textAlign: 'right' }),
                                }}
                            />
                        </div>
                    )}
                    {isShow('currency') && (
                        <div className="col-md-3 mb-3">
                            <label className="form-label">نوع ارز {isRequired('currency') && !readOnly && <span className="text-danger">*</span>}</label>
                            <Select
                                classNamePrefix="react-select"
                                options={currencies.map(c => ({ value: c.symbol, label: `${c.fa_name} (${c.symbol})` }))}
                                value={currencies.find(c => c.symbol === newItem.currencyType) ? { value: newItem.currencyType, label: `${currencies.find(c => c.symbol === newItem.currencyType).fa_name} (${newItem.currencyType})` } : { value: 'IRR', label: 'ریال (IRR)' }}
                                onChange={(opt) => setNewItem({ ...newItem, currencyType: opt ? opt.value : 'IRR' })}
                                placeholder="انتخاب کنید..."
                                isDisabled={readOnly}
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        borderColor: '#dfe3e7',
                                        borderRadius: '0.375rem',
                                        minHeight: '38px',
                                        backgroundColor: readOnly ? '#e9ecef' : '#fff',
                                        direction: 'rtl',
                                        textAlign: 'right',
                                    }),
                                    menu: (base) => ({ ...base, zIndex: 9999, direction: 'rtl', textAlign: 'right' }),
                                    option: (base) => ({ ...base, direction: 'rtl', textAlign: 'right' }),
                                }}
                            />
                        </div>
                    )}
                </div>

                <div className="row">
                    {/* Main item quantifiers */}
                    {isShow('amount') && (
                        <div className="col-md-4 mb-3">
                            <label className="form-label">تعداد/مقدار {isRequired('amount') && !readOnly && <span className="text-danger">*</span>}</label>
                            <input type="text" inputMode="decimal" className="form-control" name="amount" value={newItem.amount === 0 || newItem.amount === '' ? '' : newItem.amount} onChange={(e) => setNewItem({ ...newItem, amount: parseAmountInput(e.target.value), count: null })} placeholder="0" disabled={readOnly} />
                        </div>
                    )}
                    {isShow('unitPrice') && (
                        <div className="col-md-4 mb-3">
                            <label className="form-label">مبلغ واحد (ریال) {isRequired('unitPrice') && !readOnly && <span className="text-danger">*</span>}</label>
                            <input type="number" onWheel={(e) => e.target.blur()} min="0" step="any" className="form-control" name="unitPrice" value={stripLeadingZeros(newItem.unitPrice)} onChange={(e) => setNewItem({ ...newItem, unitPrice: parseNumericInput(e.target.value) })} disabled={readOnly} />
                        </div>
                    )}
                    {isShow('weight') && (
                        <div className="col-md-4 mb-3">
                            <label className="form-label">وزن خالص {isRequired('weight') && !readOnly && <span className="text-danger">*</span>}</label>
                            <input type="number" className="form-control" name="weight" value={newItem.weight || ''} onChange={(e) => setNewItem({ ...newItem, weight: e.target.value })} disabled={readOnly} />
                        </div>
                    )}

                    {/* Currency inputs */}
                    {(isShow('currencyAmount') || isShow('currencyPrice') || isShow('rialValue') || isShow('currencyValue') || isShow('taxSource') || isShow('diffRialCurrency')) && (
                        <div className="col-12"><h6 className="mt-3 mb-2 text-muted fw-bold border-bottom pb-2">اطلاعات ارزی</h6></div>
                    )}
                    {isShow('currencyAmount') && (
                        <div className="col-md-4 mb-3">
                            <label className="form-label">میزان ارز {isRequired('currencyAmount') && !readOnly && <span className="text-danger">*</span>}</label>
                            <input type="number" className="form-control" name="currencyAmount" value={newItem.currencyAmount || ''} onChange={(e) => setNewItem({ ...newItem, currencyAmount: e.target.value })} disabled={readOnly} />
                        </div>
                    )}
                    {isShow('currencyPrice') && (
                        <div className="col-md-4 mb-3">
                            <label className="form-label">نرخ برابری ارز با ریال / نرخ فروش ارز {isRequired('currencyPrice') && !readOnly && <span className="text-danger">*</span>}</label>
                            <input type="number" className="form-control" name="currencyPrice" value={newItem.currencyPrice || ''} onChange={(e) => setNewItem({ ...newItem, currencyPrice: e.target.value })} disabled={readOnly} />
                        </div>
                    )}
                    {isShow('rialValue') && (
                        <div className="col-md-4 mb-3">
                            <label className="form-label">ارزش ریالی کالا {isRequired('rialValue') && !readOnly && <span className="text-danger">*</span>}</label>
                            <input type="number" className="form-control" name="rialValue" value={newItem.rialValue || ''} onChange={(e) => setNewItem({ ...newItem, rialValue: e.target.value })} disabled={readOnly} />
                        </div>
                    )}
                    {isShow('currencyValue') && (
                        <div className="col-md-4 mb-3">
                            <label className="form-label">ارزش ارزی کالا {isRequired('currencyValue') && !readOnly && <span className="text-danger">*</span>}</label>
                            <input type="number" className="form-control" name="currencyValue" value={newItem.currencyValue || ''} onChange={(e) => setNewItem({ ...newItem, currencyValue: e.target.value })} disabled={readOnly} />
                        </div>
                    )}
                    {isShow('taxSource') && (
                        <div className="col-md-4 mb-3">
                            <label className="form-label">ماخذ مالیات بر ارزش افزوده در الگوی فروش ارز {isRequired('taxSource') && !readOnly && <span className="text-danger">*</span>}</label>
                            <input type="number" className="form-control" name="taxSource" value={newItem.taxSource || ''} onChange={(e) => setNewItem({ ...newItem, taxSource: e.target.value })} disabled={readOnly} />
                        </div>
                    )}
                    {isShow('diffRialCurrency') && (
                        <div className="col-md-4 mb-3">
                            <label className="form-label">تفاوت نرخ خرید و فروش ارز / کارمزد {isRequired('diffRialCurrency') && !readOnly && <span className="text-danger">*</span>}</label>
                            <input type="number" className="form-control" name="diffRialCurrency" value={newItem.diffRialCurrency || ''} onChange={(e) => setNewItem({ ...newItem, diffRialCurrency: e.target.value })} disabled={readOnly} />
                        </div>
                    )}

                    {/* Standard Pricing */}
                    <div className="col-12"><h6 className="mt-3 mb-2 text-muted fw-bold border-bottom pb-2">محاسبات ارزش و تخفیف</h6></div>
                    <div className="col-md-4 mb-3">
                        <label className="form-label">مبلغ قبل از تخفیف</label>
                        <input type="text" className="form-control" value={calculateItemCosts(newItem).priceBeforeSale.toLocaleString('fa')} disabled />
                    </div>
                    {isShow('salePrice') && (
                        <div className="col-md-4 mb-3">
                            <label className="form-label">مبلغ تخفیف (ریال) {isRequired('salePrice') && !readOnly && <span className="text-danger">*</span>}</label>
                            <input type="number" onWheel={(e) => e.target.blur()} min="0" step="any" className="form-control" name="salePrice" value={stripLeadingZeros(newItem.salePrice)} onChange={(e) => setNewItem({ ...newItem, salePrice: parseNumericInput(e.target.value) })} disabled={readOnly} />
                        </div>
                    )}
                    <div className="col-md-4 mb-3">
                        <label className="form-label">مبلغ بعد از تخفیف</label>
                        <input type="text" className="form-control" value={calculateItemCosts(newItem).priceAfterSale.toLocaleString('fa')} disabled />
                    </div>

                    {/* Taxes & Other Legal Funds */}
                    <div className="col-12"><h6 className="mt-3 mb-2 text-muted fw-bold border-bottom pb-2">مالیات و وجوه قانونی</h6></div>
                    {isShow('taxPercent') && (
                        <div className="col-md-4 mb-3">
                            <label className="form-label">نرخ مالیات بر ارزش افزوده (%) {isRequired('taxPercent') && !readOnly && <span className="text-danger">*</span>}</label>
                            <input type="number" onWheel={(e) => e.target.blur()} min="0" max="100" step="any" className="form-control" name="taxPercent" value={stripLeadingZeros(newItem.taxPercent)} onChange={(e) => setNewItem({ ...newItem, taxPercent: Math.min(100, parseNumericInput(e.target.value)) })} disabled={readOnly} />
                        </div>
                    )}
                    <div className="col-md-4 mb-3">
                        <label className="form-label">مبلغ مالیات بر ارزش افزوده</label>
                        <input type="text" className="form-control" value={calculateItemCosts(newItem).taxPrice.toLocaleString('fa')} disabled />
                    </div>
                    {isShow('otherTaxS') && (
                        <div className="col-md-4 mb-3">
                            <label className="form-label">موضوع سایر مالیات و عوارض {isRequired('otherTaxS') && !readOnly && <span className="text-danger">*</span>}</label>
                            <input type="text" className="form-control" name="otherTaxS" value={newItem.otherTaxS || ''} onChange={(e) => setNewItem({ ...newItem, otherTaxS: e.target.value })} disabled={readOnly} />
                        </div>
                    )}
                    {isShow('otherTaxR') && (
                        <div className="col-md-4 mb-3">
                            <label className="form-label">نرخ سایر مالیات و عوارض (%) {isRequired('otherTaxR') && !readOnly && <span className="text-danger">*</span>}</label>
                            <input type="number" className="form-control" name="otherTaxR" value={newItem.otherTaxR || ''} onChange={(e) => setNewItem({ ...newItem, otherTaxR: e.target.value })} disabled={readOnly} />
                        </div>
                    )}
                    {(isShow('otherTaxP') || isShow('otherTax')) && (
                        <div className="col-md-4 mb-3">
                            <label className="form-label">مبلغ سایر مالیات و عوارض (ریال) {(isRequired('otherTaxP') || isRequired('otherTax')) && !readOnly && <span className="text-danger">*</span>}</label>
                            <input type="number" onWheel={(e) => e.target.blur()} min="0" step="any" className="form-control" name="otherTax" value={stripLeadingZeros(newItem.otherTax)} onChange={(e) => setNewItem({ ...newItem, otherTax: parseNumericInput(e.target.value) })} disabled={readOnly} />
                        </div>
                    )}
                    {isShow('otherLegalS') && (
                        <div className="col-md-4 mb-3">
                            <label className="form-label">موضوع سایر وجوه قانونی {isRequired('otherLegalS') && !readOnly && <span className="text-danger">*</span>}</label>
                            <input type="text" className="form-control" name="otherLegalS" value={newItem.otherLegalS || ''} onChange={(e) => setNewItem({ ...newItem, otherLegalS: e.target.value })} disabled={readOnly} />
                        </div>
                    )}
                    {isShow('otherLegalR') && (
                        <div className="col-md-4 mb-3">
                            <label className="form-label">نرخ سایر وجوه قانونی (%) {isRequired('otherLegalR') && !readOnly && <span className="text-danger">*</span>}</label>
                            <input type="number" className="form-control" name="otherLegalR" value={newItem.otherLegalR || ''} onChange={(e) => setNewItem({ ...newItem, otherLegalR: e.target.value })} disabled={readOnly} />
                        </div>
                    )}
                    {(isShow('otherLegalP') || isShow('otherLegalFunds')) && (
                        <div className="col-md-4 mb-3">
                            <label className="form-label">مبلغ سایر وجوه قانونی (ریال) {(isRequired('otherLegalP') || isRequired('otherLegalFunds')) && !readOnly && <span className="text-danger">*</span>}</label>
                            <input type="number" onWheel={(e) => e.target.blur()} min="0" step="any" className="form-control" name="otherLegalFunds" value={stripLeadingZeros(newItem.otherLegalFunds)} onChange={(e) => setNewItem({ ...newItem, otherLegalFunds: parseNumericInput(e.target.value) })} disabled={readOnly} />
                        </div>
                    )}

                    {/* Gold & Jewelry Details */}
                    {(isShow('constructionFee') || isShow('carat') || isShow('sellerProfit') || isShow('commission') || isShow('constructionFeeNum')) && (
                        <div className="col-12"><h6 className="mt-3 mb-2 text-muted fw-bold border-bottom pb-2">اطلاعات طلا و جواهر</h6></div>
                    )}
                    {isShow('constructionFee') && (
                        <div className="col-md-4 mb-3">
                            <label className="form-label">اجرت ساخت {isRequired('constructionFee') && !readOnly && <span className="text-danger">*</span>}</label>
                            <input type="number" className="form-control" name="constructionFee" value={newItem.constructionFee || ''} onChange={(e) => setNewItem({ ...newItem, constructionFee: e.target.value })} disabled={readOnly} />
                        </div>
                    )}
                    {isShow('carat') && (
                        <div className="col-md-4 mb-3">
                            <label className="form-label">عیار طلا {isRequired('carat') && !readOnly && <span className="text-danger">*</span>}</label>
                            <input type="number" className="form-control" name="carat" value={newItem.carat || ''} onChange={(e) => setNewItem({ ...newItem, carat: e.target.value })} disabled={readOnly} />
                        </div>
                    )}
                    {isShow('sellerProfit') && (
                        <div className="col-md-4 mb-3">
                            <label className="form-label">سود فروشنده {isRequired('sellerProfit') && !readOnly && <span className="text-danger">*</span>}</label>
                            <input type="number" className="form-control" name="sellerProfit" value={newItem.sellerProfit || ''} onChange={(e) => setNewItem({ ...newItem, sellerProfit: e.target.value })} disabled={readOnly} />
                        </div>
                    )}
                    {isShow('commission') && (
                        <div className="col-md-4 mb-3">
                            <label className="form-label">حق العمل {isRequired('commission') && !readOnly && <span className="text-danger">*</span>}</label>
                            <input type="number" className="form-control" name="commission" value={newItem.commission || ''} onChange={(e) => setNewItem({ ...newItem, commission: e.target.value })} disabled={readOnly} />
                        </div>
                    )}
                    {isShow('constructionFeeNum') && (
                        <div className="col-md-4 mb-3">
                            <label className="form-label">شماره قرارداد حق‌العمل کاری {isRequired('constructionFeeNum') && !readOnly && <span className="text-danger">*</span>}</label>
                            <input type="text" className="form-control" name="constructionFeeNum" value={newItem.constructionFeeNum || ''} onChange={(e) => setNewItem({ ...newItem, constructionFeeNum: e.target.value })} disabled={readOnly} />
                        </div>
                    )}
                    {(isShow('sumCSC') || isShow('constructionFee') || isShow('sellerProfit') || isShow('commission')) && (
                        <div className="col-md-4 mb-3">
                            <label className="form-label">جمع کل اجرت، سود و حق‌العمل</label>
                            <input type="text" className="form-control" value={calculateItemCosts(newItem).sumCSC.toLocaleString('fa')} disabled />
                        </div>
                    )}

                    {/* Final Totals & Contributions */}
                    <div className="col-12"><h6 className="mt-3 mb-2 text-muted fw-bold border-bottom pb-2">اطلاعات نهایی و سهم پرداخت</h6></div>
                    <div className="col-md-4 mb-3">
                        <label className="form-label fw-bold text-dark">مبلغ کل کالا/خدمت</label>
                        <input type="text" className="form-control fw-bold text-dark" value={calculateItemCosts(newItem).totalPrice.toLocaleString('fa')} disabled />
                    </div>
                    {isShow('cashContribution') && (
                        <div className="col-md-4 mb-3">
                            <label className="form-label">سهم نقدی از پرداخت {isRequired('cashContribution') && !readOnly && <span className="text-danger">*</span>}</label>
                            <input type="number" className="form-control" name="cashContribution" value={newItem.cashContribution || ''} onChange={(e) => setNewItem({ ...newItem, cashContribution: e.target.value })} disabled={readOnly} />
                        </div>
                    )}
                    {isShow('vpp') && (
                        <div className="col-md-4 mb-3">
                            <label className="form-label">سهم م.ا.ا از پرداخت {isRequired('vpp') && !readOnly && <span className="text-danger">*</span>}</label>
                            <input type="number" className="form-control" name="vpp" value={newItem.vpp || ''} onChange={(e) => setNewItem({ ...newItem, vpp: e.target.value })} disabled={readOnly} />
                        </div>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>{readOnly ? 'بستن' : 'انصراف'}</button>
                {!readOnly && <button className="btn btn-primary" onClick={handleSaveItem}>{isEditing ? 'ذخیره تغییرات' : 'افزودن'}</button>}
            </Modal.Footer>
        </Modal>
    );

    return (
        <div className="step-content">
            <h5 className="mb-4 d-flex justify-content-between align-items-center">
                مشخصات کالا/خدمت {!readOnly && <span className="text-danger">*</span>}
                {!readOnly && (
                    <button className="btn btn-primary btn-sm" onClick={() => handleOpenModal()}>
                        <i className="feather-plus me-1"></i> افزودن کالا/خدمت
                    </button>
                )}
            </h5>

            <div className="table-responsive">
                <table className="table table-hover">
                    <thead>
                        <tr>
                            <th>ردیف</th>
                            <th>شناسه</th>
                            <th style={{ maxWidth: '150px' }}>عنوان</th>
                            <th>تعداد/مقدار</th>
                            <th>مبلغ واحد</th>
                            <th>نرخ م.ا.ا</th>
                            <th>مبلغ کل</th>
                            {!readOnly && <th className="text-end">عملیات</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {(formData.productItems && formData.productItems.length > 0) ? (
                            formData.productItems.map((item, index) => (
                                <tr key={item.id} onClick={() => handleOpenModal(item)} style={{ cursor: 'pointer' }}>
                                    <td>{index + 1}</td>
                                    <td>{item.productId}</td>
                                    <td className='text-wrap' style={{ maxWidth: '150px' }}>{item.name}</td>
                                    <td>{item.amount ?? item.count} {measurementUnits.find(u => u.code === item.measurement)?.name}</td>
                                    <td>{item.unitPrice.toLocaleString('fa')}</td>
                                    <td>{item.taxPercent}%</td>
                                    <td>{item.totalPrice.toLocaleString('fa')}</td>
                                    {!readOnly && (
                                        <td className="text-end">
                                            <button
                                                className="btn btn-icon btn-sm btn-outline-danger"
                                                onClick={(e) => { e.stopPropagation(); handleRemoveItem(item.id); }}
                                            >
                                                <i className="feather-trash-2"></i>
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={readOnly ? "7" : "8"} className="text-center py-4">
                                    <i className="feather-alert-triangle text-warning me-2"></i> کالایی وارد نکردید.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className='row mt-4'>
                <div className='col-12'>
                    <div className='card'>
                        <div className='card-body'>
                            <h6 className='fw-bold border-bottom pb-2 mb-3'>مجموع هزینه‌ها (ریال)</h6>
                            <ul className='list-unstyled mb-0'>
                                <li className='d-flex flex-wrap justify-content-between align-items-center mb-2 text-dark gap-1'>
                                    <span style={{ minWidth: '180px' }}>مجموع مبلغ قبل از کسر تخفیف:</span>
                                    <span className='fw-bold'>{calculatedTotals.sumPriceBeforeSale.toLocaleString('fa')}</span>
                                </li>
                                <li className='d-flex flex-wrap justify-content-between align-items-center mb-2 text-dark gap-1'>
                                    <span style={{ minWidth: '180px' }}>مجموع تخفیفات:</span>
                                    <span className='fw-bold text-danger'>{calculatedTotals.sumSalePrice.toLocaleString('fa')}</span>
                                </li>
                                <li className='d-flex flex-wrap justify-content-between align-items-center mb-2 border-top pt-2 text-dark gap-1'>
                                    <span style={{ minWidth: '180px' }}>مجموع مبلغ پس از کسر تخفیف:</span>
                                    <span className='fw-bold'>{calculatedTotals.sumPriceAfterSale.toLocaleString('fa')}</span>
                                </li>
                                <li className='d-flex flex-wrap justify-content-between align-items-center mb-2 text-dark gap-1'>
                                    <span style={{ minWidth: '180px' }}>مجموع مالیات بر ارزش افزوده:</span>
                                    <span className='fw-bold'>{calculatedTotals.sumTaxPrice.toLocaleString('fa')}</span>
                                </li>
                                <li className='d-flex flex-wrap justify-content-between align-items-center mb-2 text-dark gap-1'>
                                    <span style={{ minWidth: '180px' }}>مجموع سایر مالیات و عوارض:</span>
                                    <span className='fw-bold'>{calculatedTotals.sumOtherTax.toLocaleString('fa')}</span>
                                </li>
                                <li className='d-flex flex-wrap justify-content-between align-items-center mb-2 text-dark gap-1'>
                                    <span style={{ minWidth: '180px' }}>مجموع سایر وجوه قانونی:</span>
                                    <span className='fw-bold'>{calculatedTotals.sumOtherLegalFunds?.toLocaleString('fa') || 0}</span>
                                </li>
                                <li className='d-flex flex-wrap justify-content-between align-items-center fs-5 border-top border-2 pt-2 text-primary gap-1'>
                                    <span className='fw-bold' style={{ minWidth: '180px' }}>مجموع صورتحساب:</span>
                                    <span className='fw-bolder'>{calculatedTotals.sumTotalPrice.toLocaleString('fa')}</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {renderAddItemModal()}
        </div>
    );
}

export default ItemsAndServicesStep;